import { Action } from 'redux';
import { IPaymentResult } from 'types';
import {
  verifyTransactionBySessionId,
  getNewPurchaseUrlBySessionId,
  createTurnersPaymentIntent,
} from '../../services';

export enum Types {
  PAYMENT_ERROR = 'PAYMENT_ERROR',
  PAYMENT_LOADING = 'PAYMENT_LOADING',
  PAYMENT_RESET = 'PAYMENT_RESET',
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  WINDCAVE_VERIFY_PAYMENT_LOADED = 'WINDCAVE_VERIFY_PAYMENT_LOADED',
  WINDCAVE_VERIFY_PAYMENT_ERROR = 'WINDCAVE_VERIFY_PAYMENT_ERROR',
}

export interface IPaymentLoading extends Action {
  type: Types.PAYMENT_LOADING;
  data: boolean;
}

export const PaymentLoading = (isLoading: boolean): IPaymentLoading => ({
  type: Types.PAYMENT_LOADING,
  data: isLoading,
});

export interface IPaymentReset extends Action {
  type: Types.PAYMENT_RESET;
}

export const PaymentReset = (): IPaymentReset => ({
  type: Types.PAYMENT_RESET,
});

export interface IPaymentError extends Action {
  type: Types.PAYMENT_ERROR;
  data: string;
}

export const PaymentError = (data: string): IPaymentError => ({
  type: Types.PAYMENT_ERROR,
  data,
});

export interface IPaymentSuccess extends Action {
  type: Types.PAYMENT_SUCCESS;
  data: IPaymentResult;
}

export const PaymentSuccess = (data: IPaymentResult): IPaymentSuccess => ({
  type: Types.PAYMENT_SUCCESS,
  data,
});

export interface IVerifyWindcavePaymentLoaded extends Action {
  type: Types.WINDCAVE_VERIFY_PAYMENT_LOADED;
  data: any;
}

export const VerifyWindcavePaymentLoaded = (
  data: any,
): IVerifyWindcavePaymentLoaded => ({
  type: Types.WINDCAVE_VERIFY_PAYMENT_LOADED,
  data,
});

export interface IVerifyWindcavePaymentFailed extends Action {
  type: Types.WINDCAVE_VERIFY_PAYMENT_ERROR;
  data: string;
}

export const VerifyWindcavePaymentFailed = (
  data: string,
): IVerifyWindcavePaymentFailed => ({
  type: Types.WINDCAVE_VERIFY_PAYMENT_ERROR,
  data,
});

export const VerifyWindcavePayment =
  (sessionId: string, policyProvider: string) => async (dispatch) => {
    dispatch(PaymentLoading(true));
    try {
      const res = await verifyTransactionBySessionId(sessionId, policyProvider);
      dispatch(VerifyWindcavePaymentLoaded(res.data));
    } catch (err) {
      console.error('Verification failed:', err);
      dispatch(VerifyWindcavePaymentFailed('Verification failed.'));
    } finally {
      dispatch(PaymentLoading(false));
    }
  };

export const GetNewPurchaseUrlBySessionId =
  (sessionId: string, providerName: string = "protecta") => async (dispatch) => {
    dispatch(PaymentLoading(true));
    try {
      const res = await getNewPurchaseUrlBySessionId(sessionId, providerName);
      const paymentLink = res.data.paymentLinks.find(
        (l) => l.method === 'REDIRECT',
      );

      if (paymentLink && typeof window !== 'undefined') {
        window.location.replace(paymentLink.href);
      } else {
        throw new Error('Payment link not found.');
      }
    } catch (err) {
      console.error('Error getting new purchase URL:', err);
      dispatch(VerifyWindcavePaymentFailed('Could not retrieve purchase URL.'));
    } finally {
      dispatch(PaymentLoading(false));
    }
  };

const startStripePayment = async ({
  stripe,
  dispatch,
  cardElement,
  billing_details,
  createPaymentIntent,
}: {
  stripe: any;
  dispatch: any;
  cardElement: any;
  billing_details: any;
  createPaymentIntent: (paymentMethodId: string) => Promise<any>;
}) => {
  dispatch(PaymentLoading(true));
  try {
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details,
    });

    if (error) {
      dispatch(PaymentError(error.message));
      return;
    }

    const { data } = await createPaymentIntent(paymentMethod.id);

    const confirmPaymentResult = await stripe.confirmCardPayment(
      data.clientSecret,
      {
        payment_method: {
          card: cardElement,
          billing_details,
        },
      },
    );

    if (confirmPaymentResult.error) {
      dispatch(PaymentError(confirmPaymentResult.error.message));
    } else {
      dispatch(
        PaymentSuccess({
          ...confirmPaymentResult.paymentIntent,
          cardType: paymentMethod.card.brand,
          name: paymentMethod.billing_details.name,
        }),
      );
    }
  } catch (err) {
    dispatch(PaymentError('Payment failed: ' + err.message));
  } finally {
    dispatch(PaymentLoading(false));
  }
};

export const PurchaseTurnersQuote =
  (stripe: any, { cardElement, billing_details, ...payload }: any) =>
    async (dispatch) => {
      startStripePayment({
        stripe,
        dispatch,
        cardElement,
        billing_details,
        createPaymentIntent: (paymentMethodId: string) =>
          createTurnersPaymentIntent({
            paymentMethodId,
            ...payload,
          }),
      });
    };
